import * as React from 'react';
import type { ButtonFullProps } from '../Button/Button';
import type { DropdownPopupContext, DropdownPopupProps, DropdownPopupRenderTrigger } from '../DropdownPopup/DropdownPopup';
import DropdownPopup from '../DropdownPopup/DropdownPopup';
import List from '../List/List';
import type { ListItemSize } from '../List/ListItem/ListItem';
import ListItem from '../List/ListItem/ListItem';
import Styles from './more-list-button.module.scss';

export type MoreListButtonOption<T extends string | number> = {
    id: T;
    label: React.ReactNode;
    leftView?: React.ReactNode;
    rightView?: React.ReactNode;
    disabled?: boolean;
    dataTip?: string;
};

export type MoreListButtonProps<T extends string | number> = Pick<DropdownPopupProps, 'popperProps'> & {
    className?: string;
    options?: MoreListButtonOption<T>[];
    preventToggleOnChange?: boolean;
    onChange?: (option: MoreListButtonOption<T>, e: React.MouseEvent<Element, MouseEvent>) => void;
    menuHost?: HTMLElement;
    children?: React.ReactNode;
    size?: ListItemSize;
    itemClassName?: string;
} & (
        | { buttonProps?: Partial<Omit<ButtonFullProps, 'onChange'>> }
        | {
              renderTrigger: DropdownPopupRenderTrigger;
          }
    );

const MoreListButton = <T extends string | number>(props: MoreListButtonProps<T>, ref: React.ForwardedRef<HTMLDivElement>) => {
    const { options, onChange, size = 'medium', ...dropdownProps } = props;
    const dropdownPopupContext = React.useRef<DropdownPopupContext>(null);
    return (
        <DropdownPopup
            ref={ref}
            {...dropdownProps}
            contextRef={dropdownPopupContext}
            popperProps={{
                modifiers: [
                    {
                        name: 'computeStyles',
                        options: {
                            // so popper will render using inset instead of transform, which fixes position fixed interference with dropdown popup
                            // and possible image/markdown modals
                            gpuAcceleration: false
                        }
                    }
                ]
            }}
        >
            {options && (
                <List className={Styles['list']}>
                    {options.map((option) => (
                        <ListItem
                            key={option.id}
                            Control="button"
                            text={option.label}
                            leftView={option.leftView}
                            size={size}
                            rightView={option.rightView}
                            disabled={option.disabled}
                            dataTip={option.dataTip}
                            className={props.itemClassName}
                            onClick={(e) => {
                                if (!props.preventToggleOnChange) {
                                    dropdownPopupContext.current?.togglePopup(false);
                                }
                                onChange?.(option, e);
                            }}
                        />
                    ))}
                </List>
            )}
            {props.children}
        </DropdownPopup>
    );
};

export default React.forwardRef(MoreListButton) as <T extends string | number>(
    p: MoreListButtonProps<T> & React.RefAttributes<HTMLDivElement>
) => React.ReactElement | null;
